import { capture } from '@parcel/analytics/browser';
import { PLAN_DEFAULTS, billedPlan } from '@parcel/types';
import { Badge, Button, Skeleton } from '@parcel/ui';
import { useRouterState } from '@tanstack/react-router';
import { ChevronRight, CreditCard } from 'lucide-react';
import * as React from 'react';
import {
  type BillingPlan,
  type PaidBillingPlan,
  type WorkspaceBilling,
  createBillingCheckoutSession,
  createBillingPortalSession,
  getWorkspaceBilling,
  isPaidBillingPlan,
} from '../../lib/billing.js';
import { isSpecSurfaceVisible } from '../../lib/spec-config.js';
import { useSpecLaunchState } from '../../lib/spec-launch.js';
import {
  type WorkspaceSummary,
  getWorkspace,
  getWorkspaceSlugFromPath,
} from '../../lib/workspaces.js';
import { ManageSeatsDialog } from '../billing/manage-seats-dialog.js';
import { BillingCreditsSection, BillingCreditsSectionSkeleton } from './billing-credits-section.js';
import { BillingPlansView } from './billing-plans-view.js';
import {
  canShowBillingPortalButton,
  getBillingErrorMessage,
  getBillingStatusLabel,
  getRenewalText,
} from './billing-settings.js';
import { SettingsPageHeader, SettingsPanel, SettingsShell } from './settings-shell.js';

/**
 * Renders a placeholder only where the real component would have rendered.
 *
 * The credits section is Spec-gated, but the page cannot mount it before it
 * knows the workspace id -- so its skeleton stands in for it, and has to answer
 * to the same gate. Without this, a workspace with Spec dark would pulse a
 * section into existence and then remove it.
 */
function SpecGatedSkeleton({ children }: { children: React.ReactNode }) {
  const launchState = useSpecLaunchState();
  if (!isSpecSurfaceVisible(launchState)) return null;
  return <>{children}</>;
}

export function BillingSettingsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const [workspace, setWorkspace] = React.useState<WorkspaceSummary | null>(null);
  const [billing, setBilling] = React.useState<WorkspaceBilling | null>(null);
  const [loadingBilling, setLoadingBilling] = React.useState(true);
  const [billingError, setBillingError] = React.useState<string | null>(null);
  const [pendingPlan, setPendingPlan] = React.useState<PaidBillingPlan | null>(null);
  const [openingPortal, setOpeningPortal] = React.useState(false);
  const [plansOpen, setPlansOpen] = React.useState(false);
  const [manageSeatsOpen, setManageSeatsOpen] = React.useState(false);
  const requestRef = React.useRef(0);
  // Hoisted rather than called beside the flag it feeds: a hook buried in a
  // derived-value block is one refactor away from ending up behind a branch.
  const specVisible = isSpecSurfaceVisible(useSpecLaunchState());

  React.useEffect(() => {
    let active = true;
    const requestId = ++requestRef.current;
    setWorkspace(null);
    setBilling(null);
    setBillingError(null);
    setLoadingBilling(true);
    setPendingPlan(null);
    setOpeningPortal(false);
    setPlansOpen(false);
    setManageSeatsOpen(false);

    async function loadBilling() {
      if (!workspaceSlug) {
        setBillingError('Select a workspace before viewing billing.');
        setLoadingBilling(false);
        return;
      }

      try {
        const loadedWorkspace = await getWorkspace(workspaceSlug);
        const loadedBilling = await getWorkspaceBilling(loadedWorkspace.id);
        if (!active || requestRef.current !== requestId) return;
        setWorkspace(loadedWorkspace);
        setBilling(loadedBilling);
        setBillingError(null);
      } catch (err) {
        if (active && requestRef.current === requestId) {
          setBillingError(err instanceof Error ? err.message : 'Unable to load billing.');
        }
      } finally {
        if (active && requestRef.current === requestId) setLoadingBilling(false);
      }
    }

    void loadBilling();
    return () => {
      active = false;
    };
  }, [workspaceSlug]);

  const startCheckout = async (plan: BillingPlan) => {
    if (!workspace || !isPaidBillingPlan(plan)) return;
    // Before the await: createBillingCheckoutSession leads to a full-page
    // navigation to Stripe, and a queued event would not flush in time.
    // PLAN_DEFAULTS[plan].seats, the same source checkout_completed uses.
    // There is no seatCount binding here, and using the workspace's current
    // quantity instead would make started and completed disagree on the same
    // checkout, so the funnel would read as if the seat count changed mid-flow.
    // BillingPlan is an alias for Plan, so this indexes cleanly.
    capture(
      'checkout_started',
      { target_plan: plan, seats: PLAN_DEFAULTS[plan].seats },
      { sendInstantly: true },
    );
    setPendingPlan(plan);
    setBillingError(null);
    try {
      const session = await createBillingCheckoutSession(workspace.id, plan);
      window.location.assign(session.url);
    } catch (err) {
      setBillingError(getBillingErrorMessage(err, 'Unable to start Checkout.'));
      setPendingPlan(null);
    }
  };

  const openPortal = async () => {
    if (!workspace || !billing) return;
    setOpeningPortal(true);
    setBillingError(null);
    try {
      const session = await createBillingPortalSession(workspace.id);
      window.location.assign(session.url);
    } catch (err) {
      setBillingError(getBillingErrorMessage(err, 'Unable to open billing portal.'));
      setOpeningPortal(false);
    }
  };

  const refreshBilling = React.useCallback(async () => {
    if (!workspace) return;
    try {
      setBilling(await getWorkspaceBilling(workspace.id));
    } catch {
      // A failed refresh leaves the prior figures in place; the next load reconciles.
    }
  }, [workspace]);

  const billedPlanValue = billing
    ? billedPlan({ plan: billing.plan, status: billing.status })
    : 'free';
  // Reflect the EFFECTIVE plan (includes an admin plan_override), not just the Stripe-billed plan.
  const currentPlan = workspace?.effectivePlan ?? billedPlanValue;
  // "Complimentary" = an override grants a PAID plan with no paid subscription behind it
  // (effective is paid, billed resolves to free). A live subscription, or a downgrade override,
  // keeps the normal Stripe status + renewal badges instead.
  const isComplimentary = currentPlan !== 'free' && billedPlanValue === 'free';
  const currentPlanDefinition = billing?.plans.find((plan) => plan.plan === currentPlan);
  const renewalText = billing
    ? getRenewalText(billing.currentPeriodEnd, billing.cancelAtPeriodEnd)
    : null;
  const hasStripeSubscription = Boolean(billing?.stripeSubscriptionId);
  /**
   * Who may open `ManageSeatsDialog`, which buys BOTH seat kinds.
   *
   * `seatManagement.canManage` is billed-Pro-only, because WORKSPACE seats are.
   * Gating the whole dialog on it left a Starter owner with no way to buy,
   * reduce, or cancel an AI seat anywhere in the product -- AI seats are a
   * Starter and Pro capability, and consolidating both kinds into one dialog
   * quietly inherited the stricter of the two gates.
   *
   * Free is excluded because it cannot buy an AI seat either, and offering a
   * control whose only content is "Available on Starter and Pro" is worse than
   * offering none.
   */
  const canManageWorkspaceSeats = billing?.seatManagement.canManage ?? false;
  const canManageAiSeats =
    specVisible && (billing?.canManageBilling ?? false) && currentPlan !== 'free';
  const canOpenSeatDialog = canManageWorkspaceSeats || canManageAiSeats;
  const canOpenBillingPortal = canShowBillingPortalButton({
    stripeCustomerId: billing?.stripeCustomerId ?? null,
    canManageBilling: billing?.canManageBilling ?? false,
  });

  if (plansOpen && billing) {
    return (
      // Keyed apart from the Billing shell so the scroll position starts at the top.
      <SettingsShell key="plans">
        <BillingPlansView
          plans={billing.plans}
          currentPlan={currentPlan}
          canManageBilling={billing.canManageBilling}
          hasStripeSubscription={hasStripeSubscription}
          pendingPlan={pendingPlan}
          openingPortal={openingPortal}
          spec={specVisible}
          onBack={() => setPlansOpen(false)}
          onCheckout={(plan) => void startCheckout(plan)}
          onOpenPortal={() => void openPortal()}
        />
        {billingError ? <p className="mt-4 text-sm text-destructive">{billingError}</p> : null}
      </SettingsShell>
    );
  }

  return (
    <SettingsShell key="billing">
      <SettingsPageHeader
        eyebrow="Workspace"
        title="Billing"
        description="Review plan details, invoices, and payment settings for the workspace."
        action={
          billing ? (
            <Button
              type="button"
              variant="ghost"
              className="-mr-3 gap-1 text-sm"
              onClick={() => setPlansOpen(true)}
            >
              View all plans
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          ) : null
        }
      />
      <section className="space-y-6">
        {/*
          Plan, price and cycle read top down, so the two questions a manager
          opens this page with -- what am I on, and what does it cost -- are
          answered before anything else competes for attention. AI usage lives
          on the Usage page only; Billing is the plan and the money.
        */}
        <SettingsPanel data-testid="current-plan-section">
          <div className="flex flex-wrap items-start justify-between gap-4 p-4">
            <div className="min-w-0">
              <h3 className="text-sm font-medium text-muted-foreground">Current plan</h3>
              {/*
                A skeleton rather than the word "Loading...", which was itself
                rendered at the plan name's size and weight -- so the headline
                read as though this workspace were on a plan called Loading.
              */}
              {loadingBilling ? (
                <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <Skeleton className="h-8 w-24" />
                  <Skeleton className="h-8 w-16" />
                </div>
              ) : (
                <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-2xl font-semibold text-foreground">
                    {currentPlanDefinition?.name ?? 'Free'}
                  </span>
                  {currentPlanDefinition ? (
                    <span className="text-2xl font-semibold tabular-nums text-foreground">
                      {currentPlanDefinition.price}
                    </span>
                  ) : null}
                  {currentPlanDefinition ? (
                    <span className="text-sm text-muted-foreground">
                      per {currentPlanDefinition.interval}
                    </span>
                  ) : null}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {loadingBilling ? (
                  <>
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-32 rounded-full" />
                  </>
                ) : billing ? (
                  isComplimentary ? (
                    <Badge variant="secondary">Complimentary</Badge>
                  ) : (
                    <>
                      <Badge variant="secondary">{getBillingStatusLabel(billing.status)}</Badge>
                      {renewalText ? <Badge variant="outline">{renewalText}</Badge> : null}
                    </>
                  )
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Seats belong to the plan: this answers to `canOpenSeatDialog` and nothing Spec-gated. */}
              {canOpenSeatDialog ? (
                <Button type="button" variant="outline" onClick={() => setManageSeatsOpen(true)}>
                  Manage seats
                </Button>
              ) : null}
              {canOpenBillingPortal ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={openPortal}
                  disabled={openingPortal}
                >
                  <CreditCard className="size-4" />
                  {openingPortal ? 'Opening...' : 'Manage billing'}
                </Button>
              ) : null}
            </div>
          </div>
        </SettingsPanel>

        {/* Credits, directly under the plan: what the workspace owns and can buy. */}
        {workspace ? (
          <BillingCreditsSection workspaceId={workspace.id} onUpgrade={() => setPlansOpen(true)} />
        ) : loadingBilling ? (
          <SpecGatedSkeleton>
            <BillingCreditsSectionSkeleton />
          </SpecGatedSkeleton>
        ) : null}

        {billingError ? <p className="text-sm text-destructive">{billingError}</p> : null}

        {billing && !billing.canManageBilling ? (
          <p className="text-sm text-muted-foreground">
            You can view billing, but only workspace owners and admins can change plans.
          </p>
        ) : null}

        {workspace && billing && canOpenSeatDialog ? (
          <ManageSeatsDialog
            open={manageSeatsOpen}
            onOpenChange={setManageSeatsOpen}
            workspaceId={workspace.id}
            includedSeats={billing.seatManagement.limit - billing.seatManagement.quantity}
            currentQuantity={billing.seatManagement.quantity}
            onCommitted={refreshBilling}
            canManageWorkspaceSeats={canManageWorkspaceSeats}
          />
        ) : null}
      </section>
    </SettingsShell>
  );
}
